import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FileSpreadsheet, FileText, Calendar, ArrowLeft, Download,
  TrendingUp, TrendingDown, DollarSign, CreditCard, CheckCircle
} from 'lucide-react';
import { reportsAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { toast } from 'react-hot-toast';
import ListPageHeader from '../../components/shared/ListPageHeader';

const GSTR3BPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [gstr3bData, setGstr3bData] = useState(null);

  useEffect(() => {
    if (businessId) {
      fetchGSTR3B();
    }
  }, [month, year, businessId]);

  const fetchGSTR3B = async () => {
    try {
      setLoading(true);
      const response = await reportsAPI.gstr3b({ month, year });
      setGstr3bData(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      const response = await reportsAPI.exportReport({ report_type: 'gstr3b', format, month, year });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `GSTR3B_${month.toString().padStart(2, '0')}_${year}.${format === 'pdf' ? 'pdf' : format === 'excel' ? 'xlsx' : 'json'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const formatCompactCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  const summary = gstr3bData ? {
    outward_taxable: gstr3bData.outward_supplies?.taxable_value || 0,
    outward_igst: gstr3bData.outward_supplies?.igst || 0,
    outward_cgst: gstr3bData.outward_supplies?.cgst || 0,
    outward_sgst: gstr3bData.outward_supplies?.sgst || 0,
    inward_igst: gstr3bData.inward_supplies?.igst || 0,
    inward_cgst: gstr3bData.inward_supplies?.cgst || 0,
    inward_sgst: gstr3bData.inward_supplies?.sgst || 0,
    itc_claimed: gstr3bData.itc?.itc_claimed || 0,
    net_tax_payable: gstr3bData.tax_liability?.net_tax_payable || 0,
    output_tax: gstr3bData.tax_liability?.output_tax || 0
  } : {
    outward_taxable: 0, outward_igst: 0, outward_cgst: 0, outward_sgst: 0,
    inward_igst: 0, inward_cgst: 0, inward_sgst: 0,
    itc_claimed: 0, net_tax_payable: 0, output_tax: 0
  };

  const months = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2000, i).toLocaleString('default', { month: 'long' })
  }));
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const monthLabel = new Date(year, month - 1).toLocaleString('default', { month: 'short', year: 'numeric' });

  return (
    <div className="h-full flex flex-col" data-testid="gstr3b-page">
      {/* Premium Header */}
      <ListPageHeader
        title="GSTR-3B – Monthly Summary"
        stats={[
          { icon: TrendingUp, label: 'Output Tax', value: formatCompactCurrency(summary.output_tax), color: 'blue' },
          { icon: TrendingDown, label: 'ITC Claimed', value: formatCompactCurrency(summary.itc_claimed), color: 'purple' },
          { icon: DollarSign, label: 'Net Payable', value: formatCompactCurrency(summary.net_tax_payable), color: 'emerald' },
        ]}
        secondaryAction={{
          label: monthLabel,
          icon: Calendar,
          onClick: () => {}
        }}
      />

      {/* Controls Bar */}
      <div className="flex-shrink-0 px-2 pt-2">
        <div className="bg-white rounded-md border border-slate-200/60 shadow-sm p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/business/${businessId}/reports`)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors text-[12px] font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              
              <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-1.5">
                <Calendar className="w-4 h-4 text-slate-400" />
                <select 
                  value={month} 
                  onChange={(e) => setMonth(parseInt(e.target.value))} 
                  className="text-[12px] text-slate-600 bg-transparent border-none focus:outline-none cursor-pointer"
                >
                  {months.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                <select 
                  value={year} 
                  onChange={(e) => setYear(parseInt(e.target.value))} 
                  className="text-[12px] text-slate-600 bg-transparent border-none focus:outline-none cursor-pointer"
                >
                  {years.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button onClick={() => handleExport('excel')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50">
                <FileSpreadsheet className="w-3.5 h-3.5" />
                Excel
              </button>
              <button onClick={() => handleExport('pdf')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-rose-600 rounded-lg hover:bg-rose-700 transition-colors disabled:opacity-50">
                <FileText className="w-3.5 h-3.5" />
                PDF
              </button>
              <button onClick={() => handleExport('json')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">
                <Download className="w-3.5 h-3.5" />
                JSON
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto px-2 pb-2 pt-2">
        {loading ? (
          <div className="bg-white rounded-md border border-slate-200/60 shadow-sm flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
              <p className="text-[13px] text-slate-500 font-normal">Loading GSTR-3B...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* 3.1 - Outward & Inward Supplies */}
            <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                <h3 className="text-[13px] font-semibold text-slate-700">3.1 - Outward Supplies and Inward Supplies</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="tl-table">
                  <thead>
                    <tr>
                      <th className="tl-th tl-th-teal" style={{ width: '40%' }}>Nature of Supplies</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '15%' }}>Taxable Value (₹)</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '15%' }}>IGST (₹)</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '15%' }}>CGST (₹)</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '15%' }}>SGST (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="tl-tr hover:bg-teal-50/40">
                      <td className="tl-td">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-blue-500" />
                          <span className="text-[13px] font-medium text-slate-700">Outward Taxable Supplies</span>
                        </div>
                      </td>
                      <td className="tl-td text-right text-[13px] font-semibold text-slate-800">₹{summary.outward_taxable.toFixed(2)}</td>
                      <td className="tl-td text-right text-[13px] text-slate-700">₹{summary.outward_igst.toFixed(2)}</td>
                      <td className="tl-td text-right text-[13px] text-slate-700">₹{summary.outward_cgst.toFixed(2)}</td>
                      <td className="tl-td text-right text-[13px] text-slate-700">₹{summary.outward_sgst.toFixed(2)}</td>
                    </tr>
                    <tr className="tl-tr bg-[#FAFBFC] hover:bg-teal-50/40">
                      <td className="tl-td">
                        <div className="flex items-center gap-2">
                          <TrendingDown className="w-4 h-4 text-purple-500" />
                          <span className="text-[13px] font-medium text-slate-700">Inward Supplies (ITC Available)</span>
                        </div>
                      </td>
                      <td className="tl-td text-right text-[13px] text-slate-500">—</td>
                      <td className="tl-td text-right text-[13px] text-slate-700">₹{summary.inward_igst.toFixed(2)}</td>
                      <td className="tl-td text-right text-[13px] text-slate-700">₹{summary.inward_cgst.toFixed(2)}</td>
                      <td className="tl-td text-right text-[13px] text-slate-700">₹{summary.inward_sgst.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 4 - ITC Summary */}
            <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                <h3 className="text-[13px] font-semibold text-slate-700">4 - Input Tax Credit (ITC)</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="tl-table">
                  <thead>
                    <tr>
                      <th className="tl-th tl-th-teal" style={{ width: '70%' }}>Particulars</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '30%' }}>Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="tl-tr hover:bg-teal-50/40">
                      <td className="tl-td text-[13px] font-medium text-slate-700">Total ITC Available</td>
                      <td className="tl-td text-right text-[13px] font-semibold text-slate-800">₹{summary.itc_claimed.toFixed(2)}</td>
                    </tr>
                    <tr className="tl-tr bg-[#FAFBFC] hover:bg-teal-50/40">
                      <td className="tl-td text-[13px] font-medium text-slate-700">ITC Claimed this month</td>
                      <td className="tl-td text-right text-[13px] font-semibold text-slate-800">₹{summary.itc_claimed.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 5 - Net Tax Liability Card */}
            <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                <h3 className="text-[13px] font-semibold text-slate-700">5 - Net Tax Liability</h3>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-5 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                        <TrendingUp className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="text-[11px] font-medium text-blue-700 uppercase tracking-wider">Output Tax</span>
                    </div>
                    <div className="text-2xl font-bold text-blue-700">₹{summary.output_tax.toFixed(2)}</div>
                  </div>

                  <div className="p-5 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                        <CreditCard className="w-4 h-4 text-purple-600" />
                      </div>
                      <span className="text-[11px] font-medium text-purple-700 uppercase tracking-wider">ITC Claimed</span>
                    </div>
                    <div className="text-2xl font-bold text-purple-700">₹{summary.itc_claimed.toFixed(2)}</div>
                  </div>

                  <div className="p-5 bg-teal-50 rounded-lg border border-teal-200">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-teal-600" />
                      </div>
                      <span className="text-[11px] font-medium text-teal-700 uppercase tracking-wider">Net Tax Payable</span>
                    </div>
                    <div className="text-2xl font-bold text-teal-700">₹{summary.net_tax_payable.toFixed(2)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GSTR3BPage;
